import { queryOptions, useQueryClient } from "@tanstack/react-query";
import {
  fetchOnboarding,
  markOnboardingSeen,
  onboardingKeyFor,
  type OnboardingScreen,
  type OnboardingStatus,
} from "../api/onboarding";

/**
 * An account-level fact, so it is keyed on nothing: one entry for the whole
 * session, read by the caderno's walkthrough and by Início's welcome guard.
 *
 * `staleTime: Infinity` because it can only change in one direction and only by
 * this app doing it — the mark below writes the new value into the cache itself,
 * so there is nothing a refetch could learn. Without it, every remount of the
 * shell would re-ask the server whether to show a tour that ended weeks ago.
 */
export function onboardingOptions() {
  return queryOptions({
    queryKey: ["onboarding"],
    queryFn: fetchOnboarding,
    staleTime: Infinity,
  });
}

/**
 * Marks the caderno's walkthrough — or the hub's welcome — done — and does NOT make the caller wait for it.
 *
 * The cache is written FIRST, and that ordering is load-bearing rather than an
 * optimisation. Both callers act on the mark immediately — the caderno's
 * walkthrough disappears on the tap, and the welcome navigates into a screen
 * whose guard sends back anyone still unmarked. A mark that only landed when the
 * POST came back would leave a window where the tooltip is still up, or the
 * student is bounced into the welcome they just left. The request is then free
 * to take as long as it likes.
 *
 * A failed write is swallowed on purpose. The student has finished the tour;
 * showing them an error about it — or worse, keeping them in it — would be worse
 * than the cost of seeing it once more on their next device. The endpoint logs
 * the failure on its own side, which is where anyone would look.
 */
export function useMarkOnboardingSeen(): (screen: OnboardingScreen) => void {
  const queryClient = useQueryClient();

  return (screen: OnboardingScreen) => {
    queryClient.setQueryData<OnboardingStatus>(
      onboardingOptions().queryKey,
      (current) =>
        current === undefined
          ? undefined
          : { ...current, [onboardingKeyFor(screen)]: true }
    );

    void markOnboardingSeen(screen)
      .then((status) => {
        // The server's word replaces the optimistic guess — and fills the cache
        // in the case the line above could not, where nothing had been read yet
        queryClient.setQueryData(onboardingOptions().queryKey, status);
      })
      .catch(() => {
        // Deliberately silent — see above
      });
  };
}
